/**
 * @fileoverview App Review Prompt Hook
 * 
 * Core logic for determining when to show the in-app review prompt.
 * Checks user eligibility based on admin-configured rules.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AppReviewSettings {
  id: string;
  enabled: boolean;
  min_days_since_signup: number;
  min_sessions: number;
  min_actions: number;
  dismiss_cooldown_days: number;
  prompt_title: string;
  prompt_subtitle: string;
}

interface AppReviewResponse {
  id: string;
  user_id: string;
  rating: number | null;
  prompted_at: string;
  responded_at: string | null;
  dismissed_at: string | null;
  dismiss_count: number;
  directed_to_store: boolean;
  directed_to_feedback: boolean;
}

export function useAppReviewPrompt() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shouldShow, setShouldShow] = useState(false);

  // Fetch settings
  const { data: settings } = useQuery({
    queryKey: ['app-review-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_review_settings' as any)
        .select('*')
        .limit(1)
        .single();
      if (error) throw error;
      return data as unknown as AppReviewSettings;
    },
    staleTime: 1000 * 60 * 30,
  });

  // Fetch existing user response
  const { data: existingResponse } = useQuery({
    queryKey: ['app-review-response', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('app_review_responses' as any)
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as AppReviewResponse | null;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  });

  // Check eligibility
  useEffect(() => {
    if (!user || !settings || !settings.enabled) {
      setShouldShow(false);
      return;
    }

    // Already completed the flow
    if (existingResponse?.responded_at) {
      setShouldShow(false);
      return;
    }

    // Check dismiss cooldown
    if (existingResponse?.dismissed_at) {
      const dismissedAt = new Date(existingResponse.dismissed_at);
      const cooldownEnd = new Date(dismissedAt.getTime() + settings.dismiss_cooldown_days * 24 * 60 * 60 * 1000);
      if (new Date() < cooldownEnd) {
        setShouldShow(false);
        return;
      }
    }

    // Check eligibility criteria
    checkEligibility();
  }, [user, settings, existingResponse]);

  async function checkEligibility() {
    if (!user || !settings) return;

    try {
      // Check days since signup
      const { data: profile } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        const daysSinceSignup = (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceSignup < settings.min_days_since_signup) {
          setShouldShow(false);
          return;
        }
      }

      // Check session count
      const { count: sessionCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('event_name', 'session_start');

      if ((sessionCount ?? 0) < settings.min_sessions) {
        setShouldShow(false);
        return;
      }

      // Check action count (saves + votes + try-ons)
      const { count: actionCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .in('event_name', ['item_saved', 'poll_voted', 'tryon_started']);

      if ((actionCount ?? 0) < settings.min_actions) {
        setShouldShow(false);
        return;
      }

      setShouldShow(true);
    } catch (err) {
      console.error('Error checking review eligibility:', err);
      setShouldShow(false);
    }
  }

  // Mark as dismissed
  const dismissMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      if (existingResponse) {
        await supabase
          .from('app_review_responses' as any)
          .update({
            dismissed_at: new Date().toISOString(),
            dismiss_count: (existingResponse.dismiss_count || 0) + 1,
          } as any)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('app_review_responses' as any)
          .insert({
            user_id: user.id,
            dismissed_at: new Date().toISOString(),
            dismiss_count: 1,
          } as any);
      }
    },
    onSuccess: () => {
      setShouldShow(false);
      queryClient.invalidateQueries({ queryKey: ['app-review-response'] });
    },
  });

  // Submit rating
  const submitRatingMutation = useMutation({
    mutationFn: async ({ rating, directedToStore, directedToFeedback }: {
      rating: number;
      directedToStore?: boolean;
      directedToFeedback?: boolean;
    }) => {
      if (!user) return;
      const payload = {
        user_id: user.id,
        rating,
        responded_at: new Date().toISOString(),
        directed_to_store: directedToStore || false,
        directed_to_feedback: directedToFeedback || false,
      };

      if (existingResponse) {
        await supabase
          .from('app_review_responses' as any)
          .update(payload as any)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('app_review_responses' as any)
          .insert(payload as any);
      }
    },
    onSuccess: () => {
      setShouldShow(false);
      queryClient.invalidateQueries({ queryKey: ['app-review-response'] });
    },
  });

  return {
    shouldShow,
    settings,
    markDismissed: dismissMutation.mutate,
    submitRating: submitRatingMutation.mutate,
  };
}
